import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { linkTokenFrom, verifyToken } from '~/lib/auth';

/**
 * /auth/callback#token=… — landing page for the sign-in email's link.
 * Redeems the single-use token for a session JWT, then bounces to the
 * dashboard. The token rides in the URL fragment (it never reaches a server
 * log); an older `?token=` link still works. Either way the address bar is
 * cleared before the token is redeemed. The ran-once ref guards React
 * StrictMode double-effects: the token is single-use server-side, so a
 * second verify would 401 a valid login.
 */
export function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const ranOnce = useRef(false);

  useEffect(() => {
    if (ranOnce.current) return;
    ranOnce.current = true;

    const token = linkTokenFrom(window.location.hash, window.location.search);
    // The token has served its purpose once it leaves the address bar:
    // keep it out of history, referrers and anything that reads the URL.
    window.history.replaceState(null, '', '/auth/callback');
    if (!token) {
      setError(
        'This link is incomplete. Open the whole link from your email, or ask for a new sign-in code.',
      );
      return;
    }
    void verifyToken(token).then(({ error }) => {
      if (error) setError(error);
      else navigate('/', { replace: true });
    });
  }, [navigate]);

  return (
    <div className="trc-login-screen">
      <div className="trc-login-card">
        <p className="trc-eyebrow">The River Crew</p>
        <h1 className="trc-osw trc-login-title">Admin</h1>
        {error ? (
          <>
            <p className="trc-login-error" role="alert">
              {error}
            </p>
            <p className="trc-login-success">
              <Link to="/login">Get a new sign-in code →</Link>
            </p>
          </>
        ) : (
          <p className="trc-login-success" role="status">
            Signing you in…
          </p>
        )}
      </div>
    </div>
  );
}
